import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

export interface PedidosAtencaoExportRequest {
  dataInicio?: string | null;
  dataFim?: string | null;
  sellerIds: number[];
  orderDbIds: number[];
  filtroDescricao?: string;  isPontuacao?: boolean;}

/**
 * Payload para o novo endpoint POST api/RelatoriosPedidos/ExportarExcel.
 * Baseado nos filtros da tela — sem dependência dos IDs carregados na página.
 */
export interface ExportarPedidosRequest {
  /** ID do usuário logado */
  userId: number;
  /** "GERAL" | "DIRETORIA" */
  tipoRelatorio: string;
  /** "ENVIADOS" | "ATENCAO" | "NAO_ENVIADOS" | "PENDENCIAS" */
  tela: string;
  /* Filtros ─────────────────────────────────── */
  dataInicio?: string;
  dataFim?: string;
  cliente?: string;
  nroPedido?: number;
  status?: string;
  vendedor?: string;
  filtrarB2B?: boolean;
  filtrarCancelado?: boolean;
  numeroNf?: string;
  dtFaturamentoInicio?: string;
  dtFaturamentoFim?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RelatoriosService {
  private baseUrl: string;
  private headers = new HttpHeaders();

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') baseUrl: string,
    private toastr: ToastrService
  ) {
    this.baseUrl = baseUrl;
  }

  /**
   * Solicita a exportação em Excel dos pedidos atenção e retorna o Blob do arquivo.
   * @param payload Filtros + IDs dos pedidos exibidos na tela.
   */
  public exportarPedidosAtencaoExcel(
    payload: PedidosAtencaoExportRequest
  ): Observable<Blob> {
    return this.http
      .post(
        this.baseUrl + 'api/RelatorioPedidosAtencao/ExportarExcel',
        payload,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao gerar Excel. Contate o suporte...');
          return of(null);
        })
      ) as Observable<Blob>;
  }

  /**
   * Solicita a exportação em Excel dos pedidos enviados e retorna o Blob do arquivo.
   * @param payload Filtros + IDs dos pedidos exibidos na tela.
   */
  public exportarPedidosEnviadosExcel(
    payload: PedidosAtencaoExportRequest
  ): Observable<Blob> {
    return this.http
      .post(
        this.baseUrl + 'api/RelatorioPedidosAtencao/ExportarExcel',
        payload,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao gerar Excel. Contate o suporte...');
          return of(null);
        })
      ) as Observable<Blob>;
  }

  /**
   * Solicita a exportação em Excel dos pedidos não enviados e retorna o Blob do arquivo.
   * @param payload Filtros + IDs dos pedidos exibidos na tela.
   */
  public exportarPedidosNaoEnviadosExcel(
    payload: PedidosAtencaoExportRequest
  ): Observable<Blob> {
    return this.http
      .post(
        this.baseUrl + 'api/RelatorioPedidosAtencao/ExportarExcel',
        payload,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao gerar Excel. Contate o suporte...');
          return of(null);
        })
      ) as Observable<Blob>;
  }

  /**
   * Novo endpoint: gera Excel GERAL ou DIRETORIA baseado apenas nos filtros
   * (sem depender dos IDs atualmente carregados na tela).
   *
   * POST api/RelatoriosPedidos/ExportarExcel
   */
  public exportarExcel(payload: ExportarPedidosRequest): Observable<Blob> {
    return this.http
      .post(
        this.baseUrl + 'api/RelatoriosPedidos/ExportarExcel',
        payload,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao gerar Excel. Contate o suporte...');
          return of(null);
        })
      ) as Observable<Blob>;
  }

  /**
   * Gera Excel de Pontuações (3 abas) baseado nos filtros da tela.
   * POST api/RelatoriosPedidos/ExportarPontuacoesExcel
   */
  public exportarPontuacoesExcel(payload: ExportarPedidosRequest): Observable<Blob> {
    return this.http
      .post(
        this.baseUrl + 'api/RelatoriosPedidos/ExportarPontuacoesExcel',
        payload,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao gerar Excel de Pontuações. Contate o suporte...');
          return of(null);
        })
      ) as Observable<Blob>;
  }
}
